import { FileInputButton } from '@/components/FileInput';
import { ImageCropComponent } from '@/components/images/ImageCropComponent';
import { CreateMuscleTag, ExerciseDTO, MuscleTag } from '@/generated';
import { uploadFile } from '@/utils/api-helpers';
import { objectHasId } from '@/utils/create-mode-helper';
import { Photo } from '@mui/icons-material';
import { Autocomplete, Box, Button, Dialog, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { ExerciseCard } from './ExerciseCard';
import { AppContext } from '@/contexts/app.context';
import { SelectExercisesDrawer } from '../exercises/SelectExerciseDrawer';
import { useAllExercisesQuery, useMuscleTagsQuery } from '@/hooks/query-hooks';
import { useCreateMuscleTag, useUpdateMuscleTag } from '@/hooks/muscle-tag-mutation.hook';

type EditMuscleTagProps = {
  initialMuscleTag?: MuscleTag | CreateMuscleTag;
  muscleTagUpdated?: (muscleTag: MuscleTag | CreateMuscleTag) => void;
  onClose?: () => void;
  open: boolean;
};

export const EditMuscleTagModal = (props: EditMuscleTagProps) => {
  const { initialMuscleTag, open } = props;
  const [imageFile, setImageFile] = useState<File | null>();
  const [muscleTag, setMuscleTag] = useState<MuscleTag | CreateMuscleTag | undefined>(initialMuscleTag);
  const isEditMode = muscleTag ? objectHasId(muscleTag) : false;
  const [showAddExerciseDrawer, setShowAddExerciseDrawer] = useState<boolean>(false);
  const { exercises } = useAllExercisesQuery(props.open);
  const createMuscleTag = useCreateMuscleTag();
  const updateMuscleTag = useUpdateMuscleTag();
  const [exercise, setExercise] = useState<ExerciseDTO>();

  useEffect(() => {
    setMuscleTag(initialMuscleTag);
  }, [initialMuscleTag]);

  useEffect(() => {
    setExercise(exercises?.find((e) => e.id === muscleTag?.exemplaryExerciseId));
  }, [muscleTag, exercises]);

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={() => {
        if (props.onClose) props.onClose();
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        Edit Muscle Tag
      </DialogTitle>
      <DialogContent>
        <SelectExercisesDrawer
          show={showAddExerciseDrawer}
          setShow={setShowAddExerciseDrawer}
          exerciseSelected={(e) => {
            if (muscleTag) setMuscleTag({ ...muscleTag, exemplaryExerciseId: e.id });
          }}
        ></SelectExercisesDrawer>

        <Box sx={{ marginTop: 2, marginBottom: 2 }}>
          <ImageCropComponent imageFile={imageFile} onImageFileChanged={setImageFile} imageId={initialMuscleTag?.imageId} />
        </Box>
        <FileInputButton accept=".png,.jpg,.jpeg" startIcon={<Photo />} onFilePicked={(f) => setImageFile(f)}>
          Change Image
        </FileInputButton>
        <Stack>
          <TextField
            value={muscleTag?.name}
            placeholder="Tag Name"
            label="Tag Name"
            aria-label="Title"
            aria-describedby="basic-addon1"
            sx={{ my: 1 }}
            onChange={(e: any) => {
              if (muscleTag) setMuscleTag({ ...muscleTag, name: e.target.value });
            }}
          />
          <TextField
            multiline
            value={muscleTag?.description}
            placeholder="Description"
            label="Description"
            aria-label="Description"
            aria-describedby="basic-addon1"
            sx={{ my: 1 }}
            onChange={(e: any) => {
              if (muscleTag) setMuscleTag({ ...muscleTag, description: e.target.value });
            }}
          />
          <MuscleTagAutocomplete
            selectedMuscleTags={muscleTag?.groupMuscleTagIds ?? []}
            setSelectedMuscleTags={(mt) => {
              if (muscleTag) setMuscleTag({ ...muscleTag, groupMuscleTagIds: mt });
            }}
          />
          <Typography sx={{ my: 1 }} variant="h6">
            Exemplary Exercise
          </Typography>
          <Box sx={{ m: 1, justifyItems: 'center' }}>
            {exercise && (
              <ExerciseCard
                onClick={() => {
                  setShowAddExerciseDrawer(true);
                }}
                exercise={exercise}
                timeout={1}
              />
            )}
            <Button
              onClick={() => {
                setShowAddExerciseDrawer(true);
              }}
            >
              Add Exemplary exercise
            </Button>
          </Box>
        </Stack>

        <Box sx={{ m: 1 }}>
          <Button
            onClick={async () => {
              if (muscleTag !== undefined) {
                let mt = muscleTag;
                if (imageFile) {
                  const fileId = await uploadFile(imageFile);
                  mt = { ...muscleTag, imageId: fileId };
                }

                if (objectHasId(mt)) {
                  await updateMuscleTag.mutateAsync(mt);
                  if (props.muscleTagUpdated) props?.muscleTagUpdated(mt);
                  setImageFile(null);
                } else {
                  await createMuscleTag.mutateAsync(mt);
                  if (props.muscleTagUpdated) props?.muscleTagUpdated(mt);
                  setImageFile(null);
                }
              }

              setMuscleTag(undefined);
              if (props.onClose) props.onClose();
            }}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
          <Button
            onClick={() => {
              setMuscleTag(undefined);
              if (props.onClose) props.onClose();
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default function MuscleTagAutocomplete(props: { selectedMuscleTags: Array<string>; setSelectedMuscleTags: (ids: Array<string>) => void }) {
  const { muscleTags } = useMuscleTagsQuery();
  const { selectedMuscleTags, setSelectedMuscleTags } = props;

  return (
    <Autocomplete
      multiple
      autoHighlight
      id="tags-outlined"
      defaultValue={muscleTags?.filter((bt) => selectedMuscleTags.some((id) => id === bt.id))}
      onChange={(event, boneTags) => {
        setSelectedMuscleTags(boneTags.map((bt) => bt.id));
      }}
      options={muscleTags ?? []}
      getOptionLabel={(option) => option.name}
      filterSelectedOptions
      sx={{ mt: 1, minWidth: 200 }}
      renderInput={(params) => (
        <TextField
          variant="outlined"
          {...params}
          label={`Muscle Tags (only set this if defining a muscle group)`}
          placeholder={selectedMuscleTags.length === 0 ? `Muscle Tags` : undefined}
        />
      )}
    />
  );
}
